import { mapNotificationTemplatesFromApi, NotificationTemplate } from '@simOn/common/notifications';
import { EventDto, EventRecurrenceType, normalizeRecurrenceType } from '@simOn/event/element/models';
// eslint-disable-next-line @nx/enforce-module-boundaries
import type { EventFormDraftValue } from 'feature-event';
import { AgentEventPayload } from '../model';

export interface EventDraftSource {
  /** Card payload: complete for a create card, only the changed values (`null` = unchanged) for an update card. */
  payload: Partial<AgentEventPayload>;
  /** Payload properties the card removes (update cards). */
  clearedFields?: readonly string[];
  /** The event as it is now (update cards); its values fill whatever the payload leaves out. */
  current?: EventDto | null;
  /** The form's marker for "visible to everyone" (`ASSIGNED_FOR_ALL_VALUE` of the event feature). */
  assignedForAllValue: string;
}

/** Local `yyyy-MM-dd` of an ISO date, the way the event form shows a loaded event (`_toDateInput`). */
const toDateInput = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** "10:00:00" -> "10:00", verbatim like the form (`_fromTimeSpan`). */
const toTimeInput = (value: string | null | undefined): string => (value ? value.slice(0, 5) : '');

/**
 * Form values of the event form (`EventFormDraftValue`) for a card, mirroring `ModifyEventComponent._populate`:
 * the payload wins, a cleared field becomes empty, everything else keeps the current event's value.
 */
export const toEventFormValue = ({
  payload,
  clearedFields = [],
  current,
  assignedForAllValue
}: EventDraftSource): EventFormDraftValue => {
  const cleared = (field: keyof AgentEventPayload): boolean => clearedFields.includes(field);
  const isOpen =
    cleared('assigneeIds') || (payload.isOpen ?? (payload.assigneeIds ? false : (current?.isOpen ?? true)));
  const assigneeIds = payload.assigneeIds ?? current?.stakeholders?.map(({ userId }) => userId) ?? [];
  const recurrenceType = cleared('recurrenceType')
    ? EventRecurrenceType.None
    : normalizeRecurrenceType(payload.recurrenceType ?? current?.recurrenceType);
  const repeat = recurrenceType !== EventRecurrenceType.None;
  const dayOfMonth = cleared('recurrenceDayOfMonth')
    ? null
    : (payload.recurrenceDayOfMonth ?? current?.recurrenceDayOfMonth ?? null);

  return {
    name: payload.name ?? current?.name ?? '',
    assigneeIds: isOpen || !assigneeIds.length ? [assignedForAllValue] : assigneeIds,
    roomId: cleared('roomId') ? null : (payload.roomId ?? current?.roomId ?? null),
    description: cleared('description') ? '' : (payload.description ?? current?.description ?? ''),
    isAllDay: payload.isAllDay ?? current?.isAllDay ?? false,
    startDate: toDateInput(payload.startDate ?? current?.startDate) ?? '',
    startTime: cleared('startTime') ? '' : toTimeInput(payload.startTime ?? current?.startTime),
    endTime: cleared('endTime') ? '' : toTimeInput(payload.endTime ?? current?.endTime),
    endDate: cleared('endDate') ? null : toDateInput(payload.endDate ?? current?.endDate),
    repeat,
    frequency: repeat ? recurrenceType : null,
    weekDays: cleared('recurrenceDaysOfWeek')
      ? []
      : (payload.recurrenceDaysOfWeek ?? current?.recurrenceDaysOfWeek ?? []),
    monthlyMode: dayOfMonth != null ? 'day' : 'weekday'
  };
};

/** Reminders of the form: the card's list replaces the current one; a cleared list is empty. */
export const toEventReminders = ({ payload, clearedFields = [], current }: EventDraftSource): NotificationTemplate[] =>
  clearedFields.includes('notificationTemplates')
    ? []
    : mapNotificationTemplatesFromApi(payload.notificationTemplates ?? current?.notificationTemplates);

/** Last day of the recurrence (ISO, inclusive) the form keeps outside its controls. */
export const toEventRecurrenceEndDate = ({ payload, clearedFields = [], current }: EventDraftSource): string | null =>
  clearedFields.includes('recurrenceEndDate') ? null : (payload.recurrenceEndDate ?? current?.recurrenceEndDate ?? null);
